import React, { useState, useMemo } from "react"
import { Box, Flex, Grid, GridItem, Container, Button, useDisclosure, Image, Heading, Text, Spinner, VStack } from "@chakra-ui/react"

import ProductGiftCardDrawer from "@app/components/Product/ProductGiftCardDrawer"
import ProductGiftCardDenominations from "@app/components/Product/ProductGiftCardDenominations"
import ProductGiftcardQuickBalance from "@app/components/Product/ProductGiftcardQuickBalance"
import AccordionGroup from "@app/components/Sections/AccordionGroup/AccordionGroup"
import { useShopifyProductLive, useShopifyProductRaw, useShopifyVariants } from "@app/hooks/useShopify"
import { useGiftcard } from "@app/hooks/useGiftcard"
import { useConfigContext } from "@app/providers/config"
import type { PageProps } from "@root/types/global"
import type { Props } from "@app/templates/product"

type ProductGiftcardProps = PageProps<Props>

const ProductGiftcard: React.FC<ProductGiftcardProps> = ({ product: sanityProduct, location }) => {
  const {
    settings: { product: productConfig },
  } = useConfigContext()
  const { giftcardSettings } = useGiftcard(location)
  const { product: rawProduct } = useShopifyProductRaw(sanityProduct)
  const { product: liveProduct, loading } = useShopifyProductLive(rawProduct)
  const { isOpen, onClose, onOpen } = useDisclosure()
  const product = liveProduct || rawProduct
  const colours = ["brand.red", "brand.orange", "brand.green", "brand.teal"]
  const denominationValues = product?.options?.find((item: any) => item.name === productConfig.denomination)?.values || []
  const { handleOption, selectedOptions } = useShopifyVariants({
    firstAvailable: product?.variants?.length === 1,
    loading,
    product,
    useParameter: true,
  })

  const selectedDenomination = selectedOptions.find(option => option.name === "Denomination")?.value
  const allCategories = [...(giftcardSettings?.categories || [])]
  const firstAvailableCategoryName = allCategories?.[0]?.category
  const [selectedCategory, setSelectedCategory] = useState<string>(firstAvailableCategoryName || "")
  const initialDesigns =
    giftcardSettings?.categories
      ?.find(item => item?.category === selectedCategory)
      ?.options?.filter((option): option is string => option !== null) || []
  const [currentDesignValues, setCurrentDesignValues] = useState<string[]>(initialDesigns)

  const filteredVariants = useMemo(() => {
    if (!product) return []

    const seenImages: string[] = []
    return product.variants.filter(variant => {
      const designOption = variant.selectedOptions.find(option => option.name === "Design")
      const imageSrc = variant.image?.src

      if (designOption && currentDesignValues.includes(designOption.value) && imageSrc && !seenImages.includes(imageSrc)) {
        seenImages.push(imageSrc)
        return true
      }

      return false
    })
  }, [product, currentDesignValues])

  const handleCategoryChange = (newCategory: string) => {
    setSelectedCategory(newCategory)
    const category = giftcardSettings?.categories?.find(item => item?.category === newCategory)
    const filteredOptions = category?.options?.filter((option): option is string => option !== null) || []
    setCurrentDesignValues(filteredOptions)
  }

  return (
    <Box mt="5">
      <ProductGiftCardDrawer
        product={product}
        loading={loading}
        selectedOptions={selectedOptions}
        isOpen={isOpen}
        onClose={onClose}
        denominationValues={denominationValues}
        selectedDenomination={selectedDenomination}
        handleOption={handleOption}
        colours={colours}
        location={location}
      />
      <Container maxWidth="100%" paddingInlineStart={{ base: "6", md: "14" }} paddingInlineEnd={{ base: "6", md: "14" }}>
        <Flex direction="column" alignItems="center" mb="10" textAlign="center">
          {giftcardSettings?.giftcardTitle && (
            <Heading variant="h1" fontWeight={700} mb="0.75" fontSize={{ base: "2xl", md: "4xl" }}>
              {giftcardSettings.giftcardTitle}
            </Heading>
          )}

          {giftcardSettings?.giftcardDescription && (
            <Text variant="text16" maxWidth="87.5" lineHeight="6">
              {giftcardSettings.giftcardDescription}
            </Text>
          )}
        </Flex>

        <Flex direction="column" alignItems="center">
          {giftcardSettings?.step1 && (
            <Text variant="text18" fontWeight={700} mb="3">
              {giftcardSettings.step1}
            </Text>
          )}
          <Box mb="10">
            <Grid width="100%" rowGap="3" gap="4" templateColumns={{ base: "repeat(2, 1fr)", xs: "repeat(4, 1fr)" }}>
              <ProductGiftCardDenominations
                denominationValues={denominationValues}
                selectedDenomination={selectedDenomination}
                handleOption={handleOption}
                colours={colours}
              />
            </Grid>
          </Box>

          <Flex direction="column" alignItems="center" mb="3">
            {giftcardSettings?.step1 && (
              <Text variant="text18" fontWeight={700} mb="3">
                {giftcardSettings.step2}
              </Text>
            )}

            <Flex flexWrap="wrap" justifyContent="center" paddingX="8">
              {allCategories.map((item, index) => (
                <Button
                  key={index}
                  variant="fill"
                  py="2"
                  px="3"
                  minWidth="7.5"
                  textTransform="uppercase"
                  fontWeight={500}
                  bg={selectedCategory === item?.category ? "brand.darkBlue" : "brand.white"}
                  color={selectedCategory === item?.category ? "brand.white" : "brand.darkBlue"}
                  fontSize={{ base: "xs", md: "sm" }}
                  lineHeight="4"
                  _hover={{ opacity: 0.7 }}
                  onClick={() => handleCategoryChange(item?.category || "")}
                >
                  {item?.category}
                </Button>
              ))}
            </Flex>
          </Flex>

          <Box mb="20">
            {loading && (
              <VStack alignItems="center" justifyContent="center" flex={1} pt="10" width="100%">
                <Spinner />
              </VStack>
            )}
            <Grid gridGap="3.75" gridTemplateColumns={{ base: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }}>
              {filteredVariants &&
                filteredVariants.map(variant => {
                  return (
                    <GridItem key={variant.id}>
                      <Box position="relative" _hover={{ "> div": { opacity: 1 } }}>
                        <Image src={variant.image?.src || ""} alt={variant.image?.alt || ""} />
                        {giftcardSettings?.designHoverLabel && (
                          <Box
                            position="absolute"
                            top="0"
                            right="0"
                            bottom="0"
                            left="0"
                            bg="rgba(0,0,0,0.4)"
                            opacity="0"
                            transition="opacity 0.2s"
                            display="flex"
                            alignItems="flex-end"
                            justifyContent="center"
                            _hover={{ opacity: 1 }}
                            cursor="pointer"
                            padding="4.5"
                            onClick={() => {
                              const designValue = variant.selectedOptions.find(option => option.name === "Design")?.value
                              handleOption({
                                name: productConfig.design,
                                value: designValue,
                              })
                              onOpen()
                            }}
                          >
                            <Button variant="fillWhite" fontSize="sm" py="0" textTransform="uppercase" width="100%">
                              {giftcardSettings.designHoverLabel}
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </GridItem>
                  )
                })}
            </Grid>
          </Box>
        </Flex>
      </Container>

      <ProductGiftcardQuickBalance location={location} />

      {giftcardSettings?.gcFaq && (
        <Container maxWidth="100%">
          <AccordionGroup {...giftcardSettings.gcFaq} product={product} />
        </Container>
      )}
    </Box>
  )
}

export default ProductGiftcard
